import React, {useCallback, useEffect, useState, useMemo} from "react";
import {useOutletContext} from "react-router-dom";
import {Table} from "antd";
import {useTranslation} from "react-i18next";
import {APIv1} from "../../api";
import {extractDateBySecond, getFormattedPeriod} from "../../utils";
import UzumS2PLogsColumns from "./uzumlogss2p.constants";
import S2PCountCard from "../../components/PaymentLogs/S2P/Analysis/S2PCard/S2PCountCard";
import S2PFinancialCard from "../../components/PaymentLogs/S2P/Analysis/S2PCard/S2PFinancialCard";
import AnalysisLineChart from "../../components/PaymentLogs/S2P/Analysis/AnalysisLineChart/AnalysisLineChart";
import dayjs from "dayjs";
import {ConvertCardType} from "../../utils/logsUtils";
import useUrlTableState from "../../hooks/useUrlTableState";
import ListStateIndicator from "../../components/ListStateIndicator/ListStateIndicator";

const S2P_FILTER_PARAM_MAP = {
    status: 'status',
};

const S2P_FILTER_LABELS = {
    status: 'Status',
};

const UzumLogsS2P = ({defaultPaginationSize = 20, companyInn}) => {
    const {t} = useTranslation();
    const {searchText} = useOutletContext();
    const [startPeriod, setStartPeriod] = useState(dayjs().format('YYYY-MM-DD'));
    const [endPeriod, setEndPeriod] = useState(dayjs().add(1, 'days').format('YYYY-MM-DD'));
    const [userData, setUserData] = useState(() => JSON.parse(localStorage.getItem('user') || '{}'));
    const [logsData, setLogsData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalLogs, setTotalLogs] = useState(0);
    const [dateRange, setDateRange] = useState({start: 0, end: 0});
    const tableState = useUrlTableState({
        defaultPageSize: defaultPaginationSize,
        filterParamMap: S2P_FILTER_PARAM_MAP,
        filterLabels: S2P_FILTER_LABELS,
        searchText,
    });

    const columns = useMemo(() => UzumS2PLogsColumns(t, setDateRange), [t]);
    const controlledColumns = tableState.controlledColumns(columns);

    const fetchLogsData = useCallback(async (page, pageSize, search = '', ordering = '', filters = {}) => {
        if (!userData.token) return;

        setLoading(true);
        try {
            const url = companyInn ? `/scan2pay/uzum/logs/list/?company_inn=${companyInn}` : '/scan2pay/uzum/logs/list/';
            const queryParams = {
                page,
                page_size: pageSize,
                search,
                ordering,
                ...filters,
            };

            const response = await APIv1.get(url, {
                params: queryParams,
                headers: {Authorization: `Token ${userData.token}`},
            });

            setLogsData(response.data.results.map(item => ({
                key: item.id,
                company_name: item.company_name,
                deviceSerial: item.device_serial_number === 'None' ? 'Unknown' : item.device_serial_number,
                orderId: item.order_id,
                uuid: item.uuid,
                paymentId: item.payment_id,
                amount_in_sums: item.amount_in_sums,
                cardType: item.card_type,
                status: (item.status).toUpperCase(),
                createdDate: extractDateBySecond(item.created_date),
            })));

            setTotalLogs(response.data.count);
        } catch (err) {
            console.error('Error fetching logs:', err);
        } finally {
            setLoading(false);
        }
    }, [companyInn, userData.token]);

    const tableChangeHandler = tableState.handleTableChange(columns);

    useEffect(() => {
        fetchLogsData(tableState.currentPage, tableState.pageSize, tableState.search, tableState.ordering, {
            ...tableState.filters,
            ...(dateRange.start && dateRange.end && {
                start_period: dateRange.start,
                end_period: dateRange.end,
            }),
        });
    }, [fetchLogsData, tableState.currentPage, tableState.pageSize, tableState.search, tableState.ordering, tableState.filters, dateRange]);

    const handleChangePeriod = (value) => {
        const {startPeriod, endPeriod} = getFormattedPeriod(value[0], value[1]);
        setStartPeriod(startPeriod);
        setEndPeriod(endPeriod);
    }

    return (
        <div className="content_container">
            <div className="content__analysis">
                <div className="content__analysis__column">
                    <S2PCountCard
                        startPeriod={startPeriod}
                        endPeriod={endPeriod}
                        handleChangePeriod={handleChangePeriod}
                        provider={'uzum'}
                    />
                    <S2PFinancialCard
                        startPeriod={startPeriod}
                        endPeriod={endPeriod}
                        provider={'uzum'}
                    />
                </div>
                <AnalysisLineChart provider={'uzum'}/>
            </div>
            <ListStateIndicator items={tableState.stateIndicators} onReset={tableState.resetState} onRemove={tableState.removeState}/>
            <Table
                // expandable={{
                //     expandedRowRender: record => (
                //         <p style={{margin: 0}}>{record.paymentResponse ?? record.confirmResponse}</p>
                //     ),
                //     rowExpandable: record => record.name !== 'Not Expandable',
                // }}
                columns={controlledColumns}
                dataSource={logsData}
                loading={loading}
                onChange={tableChangeHandler}
                pagination={{
                    total: totalLogs,
                    current: tableState.currentPage,
                    pageSize: tableState.pageSize,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    showTotal: (total, range) => `${range[0]} - ${range[1]} / ${total}`,
                }}
            />
        </div>
    );
};

export default UzumLogsS2P;
